import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from './api-config';

export interface AccountProfileInfoItem {
  label: string;
  value: string;
}

export interface AccountSystemCard {
  systemName: string;
  profileAssigned: string;
  branches?: string;
  companies?: string;
}

export interface AccountProfileGeneral {
  firstName: string;
  lastNamePaternal: string;
  lastNameMaternal?: string | null;
  gender?: { id: string; label: string } | null;
  email: string;
  country?: { id: string; label: string; iso?: string } | null;
  identification: {
    typeId?: string | null;
    typeLabel?: string | null;
    number: string;
  };
  employeeNumber: string;
}

export interface AccountProfileSystem {
  systemCode: string;
  systemName: string;
  profileAssigned: string;
  profileId?: string | null;
  branchesCount?: number | null;
  companiesCount?: number | null;
}

export interface AccountProfileData {
  id: string;
  fullName: string;
  photoUrl?: string | null;
  general: AccountProfileGeneral;
  systems: AccountProfileSystem[];
}

export interface AccountProfileView {
  fullName: string;
  userPhotoPath: string;
  profileData: AccountProfileInfoItem[];
  systems: AccountSystemCard[];
}

export interface AccountChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

@Injectable({ providedIn: 'root' })
export class AccountProfileService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  getMyProfile(): Observable<AccountProfileData> {
    return this.httpClient
      .get<ApiResponse<AccountProfileData>>(`${this.apiConfig.apiUrl}/auth/me/profile`)
      .pipe(
        map((response) =>
          this.unwrapData(response, 'No se pudo obtener el perfil'),
        ),
      );
  }

  uploadPhoto(file: File): Observable<AccountProfileData> {
    const formData = new FormData();
    formData.append('photo', file);
    return this.httpClient
      .put<ApiResponse<AccountProfileData>>(
        `${this.apiConfig.apiUrl}/auth/me/profile/photo`,
        formData,
      )
      .pipe(
        map((response) =>
          this.unwrapData(response, 'No se pudo actualizar la foto'),
        ),
      );
  }

  changePassword(payload: AccountChangePasswordPayload): Observable<string> {
    return this.httpClient
      .put<ApiResponse<void>>(`${this.apiConfig.apiUrl}/auth/me/password`, payload)
      .pipe(
        map((response) => response.message ?? 'Contraseña actualizada'),
      );
  }

  toView(profile: AccountProfileData): AccountProfileView {
    const general = profile.general;
    return {
      fullName: profile.fullName,
      userPhotoPath: this.resolvePhotoUrl(profile.photoUrl, profile.fullName),
      profileData: [
        { label: 'Nombre', value: general.firstName },
        { label: 'Apellido paterno', value: general.lastNamePaternal },
        {
          label: 'Apellido materno',
          value: general.lastNameMaternal?.trim() || '-',
        },
        { label: 'Género', value: general.gender?.label ?? '-' },
        { label: 'Correo electrónico', value: general.email },
        { label: 'País', value: general.country?.label ?? '-' },
        {
          label: 'Tipo de identificación',
          value: general.identification.typeLabel ?? '-',
        },
        {
          label: 'Número de identificación',
          value: general.identification.number,
        },
        { label: 'Número de legajo', value: general.employeeNumber },
      ],
      systems: profile.systems.map((system) => ({
        systemName: system.systemName,
        profileAssigned: system.profileAssigned,
        branches:
          system.branchesCount != null ? String(system.branchesCount) : undefined,
        companies:
          system.companiesCount != null ? String(system.companiesCount) : undefined,
      })),
    };
  }

  private unwrapData<T>(response: ApiResponse<T>, fallbackMessage: string): T {
    if (response.data === undefined) {
      throw new Error(response.message ?? fallbackMessage);
    }
    return response.data;
  }

  private resolvePhotoUrl(photoUrl: string | null | undefined, fullName: string): string {
    if (photoUrl?.trim()) {
      if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
        return photoUrl;
      }
      const origin = this.apiConfig.apiUrl.replace(/\/api\/?$/, '');
      return `${origin}${photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || 'U')}&size=108&background=0D47A1&color=fff`;
  }
}
